import { useState, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '../components/ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../components/ui/popover';
import { 
  Package, Plus, Search, RefreshCw, MoreVertical, 
  AlertCircle, CheckCircle, Truck, Archive, ChevronsUpDown, Check
} from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { toast } from 'sonner';
import { cn } from '../lib/utils';

const CATEGORIES = ['medical', 'office', 'cleaning', 'other'];
const PRIORITIES = ['normal', 'urgent'];
const STATUSES = ['requested', 'ordered', 'received', 'closed'];

export default function Supplies() {
  const { api, isAdmin, user } = useAuth();
  const [supplies, setSupplies] = useState([]);
  const [catalogItems, setCatalogItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterCategory, setFilterCategory] = useState('all');
  const [filterPriority, setFilterPriority] = useState('all');

  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [updateDialogOpen, setUpdateDialogOpen] = useState(false);
  const [selectedSupply, setSelectedSupply] = useState(null);
  const [formLoading, setFormLoading] = useState(false);
  
  // Searchable dropdown state
  const [itemSelectOpen, setItemSelectOpen] = useState(false);
  const [selectedCatalogItem, setSelectedCatalogItem] = useState(null);
  const [isCustomItem, setIsCustomItem] = useState(false);

  const [formData, setFormData] = useState({
    item_name: '',
    description: '',
    quantity: 1,
    category: 'medical',
    priority: 'normal',
    notes: '',
    supply_item_id: null
  });

  const [updateData, setUpdateData] = useState({
    status: '',
    notes: ''
  });

  useEffect(() => {
    fetchSupplies();
    fetchCatalog();
  }, [filterStatus, filterCategory, filterPriority]);

  const fetchSupplies = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filterStatus !== 'all') params.status = filterStatus;
      if (filterCategory !== 'all') params.category = filterCategory;
      if (filterPriority !== 'all') params.priority = filterPriority;

      const response = await api.get('/supplies', { params });
      setSupplies(response.data);
    } catch (error) {
      toast.error('Failed to fetch supplies');
    } finally {
      setLoading(false);
    }
  };

  const fetchCatalog = async () => {
    try {
      const response = await api.get('/supplies/catalog', { params: { active_only: true } });
      setCatalogItems(response.data);
    } catch (error) {
      console.error('Failed to fetch catalog:', error);
    }
  };

  const resetForm = () => {
    setFormData({
      item_name: '',
      description: '',
      quantity: 1,
      category: 'medical',
      priority: 'normal',
      notes: '',
      supply_item_id: null
    });
    setSelectedCatalogItem(null);
    setIsCustomItem(false);
  };

  const handleCatalogItemSelect = (item) => {
    if (item === 'other') {
      setIsCustomItem(true);
      setSelectedCatalogItem(null);
      setFormData({
        ...formData,
        item_name: '',
        description: '',
        category: 'other',
        supply_item_id: null
      });
    } else {
      setIsCustomItem(false);
      setSelectedCatalogItem(item);
      setFormData({
        ...formData,
        item_name: item.name,
        description: item.description || '',
        category: item.category,
        supply_item_id: item.id
      });
    }
    setItemSelectOpen(false);
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    setFormLoading(true);

    try {
      await api.post('/supplies', formData);
      toast.success('Supply request created');
      setCreateDialogOpen(false);
      resetForm();
      fetchSupplies();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create request');
    } finally {
      setFormLoading(false);
    }
  };

  const handleUpdateStatus = async () => {
    if (!selectedSupply) return;
    setFormLoading(true);

    try {
      await api.patch(`/supplies/${selectedSupply.id}`, updateData);
      toast.success('Status updated');
      setUpdateDialogOpen(false);
      fetchSupplies();
    } catch (error) {
      toast.error('Failed to update status');
    } finally {
      setFormLoading(false);
    }
  };

  const openUpdateDialog = (supply) => {
    setSelectedSupply(supply);
    setUpdateData({ status: supply.status, notes: '' });
    setUpdateDialogOpen(true);
  };

  const getStatusBadge = (status) => {
    const styles = {
      requested: 'status-requested',
      ordered: 'status-ordered',
      received: 'status-received',
      closed: 'status-closed'
    };
    return <Badge className={styles[status]}>{status}</Badge>;
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'requested': return <AlertCircle className="h-4 w-4" />;
      case 'ordered': return <Truck className="h-4 w-4" />;
      case 'received': return <CheckCircle className="h-4 w-4" />;
      case 'closed': return <Archive className="h-4 w-4" />;
      default: return null;
    }
  };

  const filteredSupplies = supplies.filter(supply => {
    const matchesSearch = 
      supply.item_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      supply.requested_by_name?.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesSearch;
  });

  const canCreateRequest = user?.role === 'employee' || user?.role === 'admin';

  // Group catalog items by category for the dropdown
  const groupedCatalogItems = CATEGORIES.reduce((acc, cat) => {
    acc[cat] = catalogItems.filter(item => item.category === cat);
    return acc;
  }, {});

  return (
    <div className="space-y-6" data-testid="supplies-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">Supplies & Inventory</h1>
          <p className="text-slate-500 mt-1">Request and track supply replenishment</p>
        </div>
        {canCreateRequest && (
          <Button 
            className="bg-teal-600 hover:bg-teal-700"
            onClick={() => {
              resetForm();
              setCreateDialogOpen(true);
            }}
            data-testid="create-supply-btn"
          >
            <Plus className="h-4 w-4 mr-2" />
            Request Supplies
          </Button>
        )}
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {STATUSES.map((status) => {
          const count = supplies.filter(s => s.status === status).length;
          return (
            <Card 
              key={status} 
              className={`cursor-pointer transition-all ${filterStatus === status ? 'ring-2 ring-teal-500' : ''}`}
              onClick={() => setFilterStatus(filterStatus === status ? 'all' : status)}
              data-testid={`stat-${status}`}
            >
              <CardContent className="pt-4 pb-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-500 capitalize">{status}</p>
                    <p className="text-2xl font-bold text-slate-900">{count}</p>
                  </div>
                  <div className={`h-10 w-10 rounded-lg flex items-center justify-center ${
                    status === 'requested' ? 'bg-yellow-100 text-yellow-600' :
                    status === 'ordered' ? 'bg-blue-100 text-blue-600' :
                    status === 'received' ? 'bg-emerald-100 text-emerald-600' :
                    'bg-slate-100 text-slate-600'
                  }`}>
                    {getStatusIcon(status)}
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Filters */}
      <Card>
        <CardHeader className="pb-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input
                placeholder="Search supplies..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
                data-testid="search-supplies"
              />
            </div>
            <Select value={filterCategory} onValueChange={setFilterCategory}>
              <SelectTrigger className="w-[150px]" data-testid="filter-category">
                <SelectValue placeholder="Category" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Categories</SelectItem>
                {CATEGORIES.map(cat => (
                  <SelectItem key={cat} value={cat} className="capitalize">{cat}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={filterPriority} onValueChange={setFilterPriority}>
              <SelectTrigger className="w-[150px]" data-testid="filter-priority">
                <SelectValue placeholder="Priority" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Priorities</SelectItem>
                {PRIORITIES.map(p => (
                  <SelectItem key={p} value={p} className="capitalize">{p}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <RefreshCw className="h-8 w-8 animate-spin text-slate-400" />
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Item</TableHead>
                    <TableHead>Quantity</TableHead>
                    <TableHead>Category</TableHead>
                    <TableHead>Priority</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Requested By</TableHead>
                    <TableHead>Date</TableHead>
                    {isAdmin && <TableHead className="text-right">Actions</TableHead>}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredSupplies.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={isAdmin ? 8 : 7} className="text-center text-slate-500 py-8">
                        No supply requests found
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredSupplies.map((supply) => (
                      <TableRow key={supply.id} data-testid={`supply-row-${supply.id}`}>
                        <TableCell>
                          <div>
                            <span className="font-medium">{supply.item_name}</span>
                            {supply.supply_item_id && (
                              <Badge variant="outline" className="ml-2 text-xs">Catalog</Badge>
                            )}
                          </div>
                          {supply.description && (
                            <p className="text-xs text-slate-500 truncate max-w-xs">{supply.description}</p>
                          )}
                        </TableCell>
                        <TableCell>{supply.quantity}</TableCell>
                        <TableCell className="capitalize">{supply.category}</TableCell>
                        <TableCell>
                          <Badge className={supply.priority === 'urgent' ? 'priority-urgent' : 'priority-normal'}>
                            {supply.priority}
                          </Badge>
                        </TableCell>
                        <TableCell>{getStatusBadge(supply.status)}</TableCell>
                        <TableCell>{supply.requested_by_name}</TableCell>
                        <TableCell>{format(parseISO(supply.created_at), 'MMM d, yyyy')}</TableCell>
                        {isAdmin && (
                          <TableCell className="text-right">
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button variant="ghost" size="icon" data-testid={`supply-actions-${supply.id}`}>
                                  <MoreVertical className="h-4 w-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                <DropdownMenuItem onClick={() => openUpdateDialog(supply)}>
                                  Update Status
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </TableCell>
                        )}
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create Dialog with Searchable Dropdown */}
      <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Request Supplies</DialogTitle>
            <DialogDescription>Select an item from the catalog or request a new item</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCreate}>
            <div className="space-y-4 py-4">
              {/* Searchable Item Selector */}
              <div className="space-y-2">
                <Label>Select Item *</Label>
                <Popover open={itemSelectOpen} onOpenChange={setItemSelectOpen}>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      role="combobox"
                      aria-expanded={itemSelectOpen}
                      className="w-full justify-between"
                      data-testid="item-selector"
                    >
                      {selectedCatalogItem 
                        ? selectedCatalogItem.name 
                        : isCustomItem 
                          ? "Other / New Item" 
                          : "Search and select an item..."}
                      <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-[450px] p-0" align="start">
                    <Command>
                      <CommandInput placeholder="Search supplies..." />
                      <CommandList>
                        <CommandEmpty>No items found</CommandEmpty>
                        {CATEGORIES.map(category => {
                          const items = groupedCatalogItems[category];
                          if (items.length === 0) return null;
                          return (
                            <CommandGroup key={category} heading={category.charAt(0).toUpperCase() + category.slice(1)}>
                              {items.map(item => (
                                <CommandItem
                                  key={item.id}
                                  value={item.name}
                                  onSelect={() => handleCatalogItemSelect(item)}
                                >
                                  <Check
                                    className={cn(
                                      "mr-2 h-4 w-4",
                                      selectedCatalogItem?.id === item.id ? "opacity-100" : "opacity-0"
                                    )}
                                  />
                                  <div className="flex flex-col">
                                    <span>{item.name}</span>
                                    {item.description && (
                                      <span className="text-xs text-slate-500 truncate max-w-[350px]">
                                        {item.description}
                                      </span>
                                    )}
                                  </div>
                                </CommandItem>
                              ))}
                            </CommandGroup>
                          );
                        })}
                        <CommandGroup heading="Other">
                          <CommandItem
                            value="other-new-item"
                            onSelect={() => handleCatalogItemSelect('other')}
                          >
                            <Check
                              className={cn(
                                "mr-2 h-4 w-4",
                                isCustomItem ? "opacity-100" : "opacity-0"
                              )}
                            />
                            <div className="flex flex-col">
                              <span className="font-medium">Other / New Item</span>
                              <span className="text-xs text-slate-500">Request an item not in the catalog</span>
                            </div>
                          </CommandItem>
                        </CommandGroup>
                      </CommandList>
                    </Command>
                  </PopoverContent>
                </Popover>
              </div>

              {/* Custom Item Name (only for "Other") */}
              {isCustomItem && (
                <div className="space-y-2">
                  <Label>Item Name *</Label>
                  <Input
                    value={formData.item_name}
                    onChange={(e) => setFormData({ ...formData, item_name: e.target.value })}
                    placeholder="Enter the item name"
                    required
                    data-testid="custom-item-name"
                  />
                </div>
              )}

              {/* Description (auto-populated or custom) */}
              {(selectedCatalogItem || isCustomItem) && (
                <div className="space-y-2">
                  <Label>Description {isCustomItem && '(Optional)'}</Label>
                  <Textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder={isCustomItem ? "Describe the item..." : ""}
                    rows={2}
                    readOnly={!isCustomItem && selectedCatalogItem}
                    className={!isCustomItem && selectedCatalogItem ? 'bg-slate-50' : ''}
                    data-testid="item-description"
                  />
                </div>
              )}

              {/* Quantity and Category */}
              {(selectedCatalogItem || isCustomItem) && (
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Quantity *</Label>
                    <Input
                      type="number"
                      value={formData.quantity}
                      onChange={(e) => setFormData({ ...formData, quantity: parseInt(e.target.value) || 1 })}
                      min="1"
                      required
                      data-testid="quantity-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Category</Label>
                    <Select 
                      value={formData.category} 
                      onValueChange={(value) => setFormData({ ...formData, category: value })}
                      disabled={!isCustomItem}
                    >
                      <SelectTrigger data-testid="category-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {CATEGORIES.map(cat => (
                          <SelectItem key={cat} value={cat} className="capitalize">{cat}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              )}

              {/* Priority */}
              {(selectedCatalogItem || isCustomItem) && (
                <div className="space-y-2">
                  <Label>Priority</Label>
                  <Select 
                    value={formData.priority} 
                    onValueChange={(value) => setFormData({ ...formData, priority: value })}
                  >
                    <SelectTrigger data-testid="priority-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="normal">Normal</SelectItem>
                      <SelectItem value="urgent">Urgent</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}

              {/* Notes */}
              {(selectedCatalogItem || isCustomItem) && (
                <div className="space-y-2">
                  <Label>Additional Notes (Optional)</Label>
                  <Textarea
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    placeholder="Any special requirements or notes..."
                    data-testid="notes-input"
                  />
                </div>
              )}
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setCreateDialogOpen(false)}>
                Cancel
              </Button>
              <Button 
                type="submit" 
                className="bg-teal-600 hover:bg-teal-700"
                disabled={formLoading || (!selectedCatalogItem && !isCustomItem) || (isCustomItem && !formData.item_name)}
                data-testid="submit-request-btn"
              >
                {formLoading ? <RefreshCw className="h-4 w-4 animate-spin" /> : 'Submit Request'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Update Status Dialog */}
      <Dialog open={updateDialogOpen} onOpenChange={setUpdateDialogOpen}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>Update Status</DialogTitle>
            <DialogDescription>
              {selectedSupply?.item_name} (Qty: {selectedSupply?.quantity})
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Status</Label>
              <Select 
                value={updateData.status} 
                onValueChange={(value) => setUpdateData({ ...updateData, status: value })}
              >
                <SelectTrigger data-testid="update-status-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {STATUSES.map(status => (
                    <SelectItem key={status} value={status} className="capitalize">{status}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Notes (Optional)</Label>
              <Textarea
                value={updateData.notes}
                onChange={(e) => setUpdateData({ ...updateData, notes: e.target.value })}
                placeholder="Add notes about this update..."
                data-testid="update-notes-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setUpdateDialogOpen(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleUpdateStatus}
              className="bg-teal-600 hover:bg-teal-700"
              disabled={formLoading}
              data-testid="update-status-btn"
            >
              {formLoading ? <RefreshCw className="h-4 w-4 animate-spin" /> : 'Update Status'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
